<script setup>
import { usePage } from '@inertiajs/vue3';
import { computed, onMounted, toValue } from 'vue';
import { watchImmediate } from '@vueuse/core';
import Header from './Partials/Header.vue';
import MobileMenu from './Partials/MobileMenu.vue';
import Footer from './Partials/Footer.vue';
import Search from './Partials/Search.vue';
import SiteHeader from '@/Components/Navigation/SiteHeader.vue';
import { useTrackEvent } from '@/Composables/gtmTrackEvent.js';

const page = usePage();

const theme = computed(() => page.props?.theme || 'defaultTheme');
const trackEvents = computed(() => page.props.trackEvents || null);

/*
 * The relaunch header has no mobile counterpart yet, so below `lg` the previous
 * bar and its drawer stay in place. Both render the structure the toggle in the
 * `settings` global selected, so only one navigation is ever served.
 */
const usesNewNavigation = computed(() => Boolean(page.props.usesNewNavigation));

onMounted(() => {
    watchImmediate(trackEvents, trackEvents => {
        const trackEventsValue = toValue(trackEvents);

        if (trackEventsValue && Array.isArray(trackEventsValue)) {
            for (const trackEvent of trackEventsValue) {
                useTrackEvent(trackEvent.key, trackEvent.value);
            }
        }
    });
});
</script>

<template>
    <div :class="theme">
        <div class="flex flex-col min-h-screen">
            <!-- Header -->
            <SiteHeader
                v-if="usesNewNavigation"
                class="hidden lg:block"
                :items="$page.props.navigations.main"
                :home-url="$page.props.homeUrl"
                :locale="$page.props.locale"
                :user="$page.props.auth?.user ?? null"
                :can-manage-api-tokens="$page.props.can.apiTokens"
                :is-impersonating="$page.props.isImpersonating"
            />

            <nav
                class="bg-deep-teal-700 purple-theme:bg-purple-700 text-white"
                :class="{ 'lg:hidden': usesNewNavigation }"
            >
                <Header />
                <MobileMenu />
            </nav>

            <!-- Page Content -->
            <main
                class="flex flex-col grow"
                role="main"
            >
                <slot />
            </main>

            <!-- Footer -->
            <Footer
                :extra-items="[{
                    class: 'ccm_overlay_trigger',
                    onclick: 'CCM?.openWidget(); return false;',
                    text: $t('components.footer.cookieConsent'),
                }]"
            />

            <!-- Environment Badge -->
            <div
                v-if="$page.props.environment !== 'production'"
                class="self-center lg:self-start fixed flex z-[1] select-none"
                aria-hidden="true"
            >
                <span
                    class="m-3 text-sm rounded-full bg-zinc-900 py-1 px-3 text-white opacity-40"
                >
                    {{ $page.props.environment }}
                </span>
            </div>

            <!-- Search -->
            <Search />
        </div>
    </div>
</template>
